import React, { useState, useEffect, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import NotificationsIcon from "@mui/icons-material/Notifications";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { Divider } from "@mui/material";
import Badge from "@mui/material/Badge";
import colors from "../../theme";
import { formatDateTime } from "../../utils/FormatDateTime";
import {
  getLatestFiveNotifications,
  markNotificationsAsRead,
} from "../../services/api";
import { useNavigate } from "react-router-dom";
import NotificationsSnackbarComp from "../NotificationsSnackbar/NotificationsSnackbar";
import { adjustTimestamp } from "../../utils/TimeStamp";

import { useDispatch } from "react-redux";
import { setNewNotificationReceived } from "../../store/notificationSlice";
import { setPayloadCurrentPower } from "../../store/payloadCurrentPowerSlice";
import io from 'socket.io-client';
import Cookies from "js-cookie";

const Notifications = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [notificationsEl, setNotificationsEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState();

  const showNotification = useCallback((content) => {
    if (content.currentPower) {
      dispatch(setPayloadCurrentPower(content.currentPower));
    }
    setNotificationMessage(content.notification.body);
    setNotificationType(content.type);
    setOpenSnackbar(true);
  }, [dispatch]);

  useEffect(() => {
    const token = Cookies.get("__auth");
    const socket = io( `${process.env.REACT_APP_API_URL}?token=${token}`)
    socket.on('chargingStatusUpdated', (message) => {
      let timeStamppedValue = adjustTimestamp(message.content.createdAt);
      const newNotification = {
        id: message.content.id,
        title: message.content.notification.title,
        message: message.content.notification.body,
        createdAt: timeStamppedValue,
        isRead: false,
        redirectTo: message.content.redirectTo || "/",
      };
      showNotification(message.content);
      setNotifications((prevNotifications) => [
        newNotification,
        ...prevNotifications.slice(0, 3),
      ]);
      setUnreadNotifications((prevCount) => prevCount + 1);
      dispatch(setNewNotificationReceived(true));
    });
    return () => {
      socket.disconnect();
    };
  }, [dispatch, showNotification]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const notificationsData = await getLatestFiveNotifications();
        const unreadCount = notificationsData.data.filter(
          (n) => !n.isRead
        ).length;
        setNotifications(notificationsData.data);
        setUnreadNotifications(unreadCount);
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchData();
  }, []);
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleNotificationsOpen = async (event) => {
    setNotificationsEl(event.currentTarget);

    try {
      await markNotificationsAsRead(notifications);

      setUnreadNotifications(0);
    } catch (error) {
      console.error("Failed to mark notifications as read:", error);
    }
  };

  const handleNotificationsClose = () => {
    setNotificationsEl(null);
    setNotifications((prevNotifications) =>
      prevNotifications.map((n) => ({ ...n, isRead: true }))
    );
  };

  const handleNotificationClick = (redirectTo) => {
    if (redirectTo === "Dashboard") {
      navigate("/");
    } else {
      navigate(redirectTo);
    }
    handleNotificationsClose();
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton color="inherit" onClick={handleNotificationsOpen}>
          <Badge
            badgeContent={unreadNotifications}
            color="primary"
            max={99}
            sx={{ bgColor: colors.mainContentBgColor }}
          >
            <NotificationsIcon sx={{ fontSize: "30px" }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={notificationsEl}
        open={Boolean(notificationsEl)}
        onClose={handleNotificationsClose}
        MenuListProps={{
          sx: {
            bgcolor: colors.mainContentBgColor,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        }}
        PaperProps={{
          sx: {
            width: {
              xs: "70vw",
              sm: "70vw",
              md: "30%",
            },
            maxWidth: {
              xs: "70vw",
              sm: "70vw",
              md: "30%",
            },
          },
        }}
      >
        {notifications.length === 0 ? (
          <MenuItem>No notifications</MenuItem>
        ) : (
          notifications.map((notification) => (
            <Box key={notification.id}>
              <MenuItem
                sx={{
                  display: "block",
                  backgroundColor: notification.isRead
                    ? colors.mainContentBgColor
                    : "#D3D3D3",
                }}
                onClick={() => handleNotificationClick(notification.redirectTo)}
              >
                <Box>
                  <Box sx={{ fontWeight: "bold" }}>{notification.title}</Box>
                  <Box
                    sx={{
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "normal",
                      fontWeight: notification.isRead ? "" : "bold",
                      fontSize: notification.isRead ? "14px" : "13px",
                    }}
                  >
                    {notification.message}
                  </Box>
                  <Box
                    sx={{
                      color: "grey",
                      whiteSpace: "nowrap",
                      fontSize: "10px",
                    }}
                  >
                    {formatDateTime(notification.createdAt)}
                  </Box>
                </Box>
              </MenuItem>
              <Divider
                sx={{
                  width: "95%",
                  bgcolor: colors.primary,
                  margin: "auto",
                }}
              />
            </Box>
          ))
        )}
      </Menu>
      <NotificationsSnackbarComp
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        errorMessage={notificationMessage}
        notificationType={notificationType}
      />
    </>
  );
};

export default Notifications;
